/**
 * Spray-bay mods (shared/garage.ts) ⇄ a car's look (src/vehicles/carTraits.ts CarLook): what garage mode
 * previews and what the online client puts on a networked car from its snapshot metadata.
 */
import type { CarLook } from '../vehicles/carTraits';
import { CAR_LOOK_KEY, CAR_LOOK_VERSION_KEY } from '../vehicles/carTraits';
import type { Vehicle } from '../vehicles/types';
import type { CarMods } from '../../shared/garage';

/** The spray-bay mods a car's look carries (stock where it carries none). */
export function modsFromLook(look: CarLook | undefined): CarMods {
  return {
    wheels: look?.wheels ?? null,
    rim: look?.rim ?? null,
    neon: look?.neon ?? null,
    ride: look?.ride === -1 || look?.ride === 1 ? look.ride : 0,
    finish: look?.matt ? 'matt' : look?.metallic === undefined ? null : look.metallic ? 'metallic' : 'gloss',
    camber: look?.camber ?? 0,
    track: look?.track ?? 0,
    livery: look?.livery ?? null,
  };
}

/** The look `base` (Des's work, plates…) with the spray-bay mods laid over it. */
export function lookWith(base: CarLook | undefined, m: CarMods): CarLook {
  const l: CarLook = { ...base };
  delete l.matt;
  if (m.finish === 'matt') l.matt = true;
  else if (m.finish === 'metallic') l.metallic = true;
  else if (m.finish === 'gloss') l.metallic = false;
  else if (base?.metallic === undefined) delete l.metallic;
  const set = <K extends 'wheels' | 'rim' | 'neon'>(k: K, v: CarLook[K] | null): void => {
    if (v === null || v === undefined) delete l[k];
    else l[k] = v;
  };
  set('wheels', m.wheels);
  set('rim', m.rim);
  set('neon', m.neon);
  if (m.ride === 0) delete l.ride;
  else l.ride = m.ride;
  if (m.camber === 0) delete l.camber;
  else l.camber = m.camber;
  if (m.track === 0) delete l.track;
  else l.track = m.track;
  if (m.livery === null) delete l.livery;
  else l.livery = m.livery;
  return l;
}


/** Put a look on a car and have its view rebuilt. */
export function putLook(v: Vehicle, look: CarLook | undefined): void {
  if (look === undefined) delete v.data[CAR_LOOK_KEY];
  else v.data[CAR_LOOK_KEY] = look;
  v.data[CAR_LOOK_VERSION_KEY] = ((v.data[CAR_LOOK_VERSION_KEY] as number | undefined) ?? 0) + 1;
}
